import { StackContext } from '@pubstack/common/src/stackContext'
import { FunctionComponent, useMemo } from 'react'
import { useContexts, useUpdateContext } from '~/api/adm/contexts.api'
import { ActionButtons, ActionsTableCell, NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import Table, { TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { Tooltip } from '~/components/Tooltip'
import { UnarchiveConfirmationModal } from '~/feature-components/UnarchiveConfirmationModal'
import { ContextConditions } from '~/modules/adm/ContextConditions'

const COLUMNS: TableColumns<StackContext> = [
  { name: 'Name', isSortable: false },
  { name: 'Conditions', isSortable: false },
  { name: 'Actions', isSortable: false },
]

/** One archived context, and the way back. Same row as the archived ad units', one column wider. */
const ArchivedContextTr: FunctionComponent<{ context: StackContext }> = ({ context }) => {
  const modal = useGlobalModal()
  const { mutateAsync: updateContext, isPending } = useUpdateContext({
    successToast: (
      <>
        <strong>{context.name}</strong> was restored.
      </>
    ),
    errorToast: (
      <>
        An error occurred while trying to restore <strong>{context.name}</strong>.
      </>
    ),
  })

  const handleUnarchive = async () => {
    if (isPending) {
      return
    }
    await updateContext({ ...context, enabled: true })
  }

  return (
    <TableRow archived>
      <TableCell>{context.name}</TableCell>
      <TableCell>
        <ContextConditions group={context.conditions} inline={true} />
      </TableCell>
      <ActionsTableCell>
        <ActionButtons>
          <Tooltip title={'Unarchive'}>
            <Button
              iconName={isPending ? 'pubstack' : 'unarchive'}
              iconSpin={isPending}
              disabled={isPending}
              variant={'tertiary'}
              title={'Unarchive'}
              onClick={() =>
                modal.open(UnarchiveConfirmationModal, {
                  name: context.name,
                  noun: 'context',
                  consequence: 'It will return to the context list and apply again to every stack that targets it.',
                  onUnarchive: handleUnarchive,
                  isPending,
                  trackingPrefix: 'adm-contexts-unarchive',
                })
              }
              trackingLabel={'adm-contexts-unarchive'}
            />
          </Tooltip>
        </ActionButtons>
      </ActionsTableCell>
    </TableRow>
  )
}

type Props = {
  search: string
}

/**
 * The archived contexts. The page chrome is the shell's; this is the table.
 *
 * It asks for every context and keeps the disabled ones, which is the one list that wants the full set —
 * everywhere else passes `enabled` and is handed the live ones.
 */
export const ArchivedContextsTable: FunctionComponent<Props> = ({ search }) => {
  const { data: contexts, isLoading } = useContexts()

  const archivedContexts = useMemo(() => contexts.filter((context) => !context.enabled), [contexts])
  // No sort: `useContexts` already hands the contexts over by name.
  const displayedContexts = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase()
    return archivedContexts.filter((context) => context.name.toLocaleLowerCase().includes(needle))
  }, [archivedContexts, search])

  if (!isLoading && archivedContexts.length === 0) {
    return <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={''} message={'No archived context.'} />
  }

  return (
    <Table columns={COLUMNS} isLoading={isLoading}>
      {displayedContexts.length > 0 ? (
        displayedContexts.map((context) => <ArchivedContextTr key={context.id} context={context} />)
      ) : (
        <TableRow>
          <NoResultsTableCell colspan={COLUMNS.length}>No results</NoResultsTableCell>
        </TableRow>
      )}
    </Table>
  )
}
